import React, { useState } from "react";

import { AlertType, ExchangeRatesTableType } from "@constants";
import { Alert, Icon, RatesDownloads } from "@partials";
import { getLocalization, fetchJSON } from "@utilities";

import {
  ExchangeRatesListPageProps,
  ExchangeTableRowItem,
  LocalizationTexts,
  TimeSelectOption,
} from "./types";

import RatesTable from "./components/RatesTable";
import RatesKBvsECB from "./components/RatesKBvsECB";

type FormWrapperProps = {
  children: React.ReactNode;
};

const EXCHANGE_RATES_FORM = "exchange-rates-form";
const DATE_PICKER = "date-picker";
const TIME_SELECT = "time-select";

const FormWrapper: React.FC<FormWrapperProps> = ({ children }) => {
  return (
    <div className="container">
      <div className="row">
        <div className="container mb-12 shadow-tab sm:shadow-none">
          <div className="row -mx-8 sm:-mx-4">
            <div className="px-0 sm:px-4">
              <div className="bg-light p-8 sm:rounded sm:px-10 sm:py-12 sm:shadow-tab md:px-16">
                <div className="shrink-0 grow-0 basis-auto">{children}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ExchangeRatesListPageLayout: React.FC<ExchangeRatesListPageProps> = ({
  api,
  cultureCode,
  localization,
  dateDll,
  timeDll,
  isQueryDate,
  items,
  downloads,
  exchangeRatesECBDifference,
  iconsList,
  baseUrl,
  queryDate,
  isSK,
}) => {
  const { calendar } = iconsList;

  const [queryDateValueString, setQueryDateValue] = useState<string>(queryDate);

  // TABS (takes selected TAB type from RatesTabs and passes it as value to RatesTable)
  const [tableType] = useState<ExchangeRatesTableType>(ExchangeRatesTableType.Wire);

  // TABLE
  const [hasTableError, setHasTableError] = useState<boolean>(false);
  const [tableItems, setTableItems] = useState<ExchangeTableRowItem[]>(items);

  // FORM
  // Form - date
  const [inputDate, setInputDate] = useState<string>(dateDll); // 'date' from config
  const [queryDateValue, setQueryDate] = useState<boolean>(isQueryDate); // boolean to show 'date error message' if data are not available for selected day
  const [queryDateText, setQueryDateText] =
    useState<keyof LocalizationTexts>("releaseTimeNoRecord"); // text for 'date error message'
  // Form - time
  const [inputTimeObject, setInputTimeObject] = useState<TimeSelectOption[]>(timeDll); // time object with all available times for the day
  const [inputTimeString, setInputTimeString] = useState<string>(timeDll[timeDll.length - 1].value); // 'time' from config (get the oldest time in the current day)

  /**
   * Handle fetch data, POST, and set payload data to items in table.
   * @remarks
   * This function fetches data only when DATE changes and the format for BE is "date".
   * @param {string} changedDate - Date selected from 'date' input.
   *
   */
  const fetchDate = (changedDate: string) => {
    const correctDateFormat = `${changedDate}`;

    fetchJSON({
      settings: {
        method: "POST",
        url: api,
        body: JSON.stringify({
          date: correctDateFormat,
          cultureCode,
          isSK: true,
        }),
      },
    })
      .then((resultData: any) => {
        if (resultData.payload.data.length > 0) {
          setHasTableError(false);
          setTableItems(resultData.payload.data);
          setInputDate(resultData.payload.dateDll);
          setInputTimeObject(resultData.payload.timeDll);
          setQueryDate(resultData.payload.isQueryDate);
          setQueryDateText(resultData.payload.localization.releaseTimeNoRecord);
          setQueryDateValue(resultData.payload.queryDate);
        } else {
          setHasTableError(true);
          throw new Error("Payload 'fetchDate' not loaded!");
        }
      })
      .catch((error) => {
        setHasTableError(true);
        if (!isProduction) {
          throw new Error(error);
        }
      });
  };

  /**
   * Handle fetch data, POST, and set payload data to items in table.
   * @remarks
   * This function fetches data when TIME changes and the format for BE is "date+time".
   * @param {string} changedDate - Date selected from 'date' input.
   * @param {string} changedTime - Time selected from 'time' select.
   *
   */
  const fetchDateTime = (changedDate: string, changedTime: string) => {
    const correctDateFormat = `${changedDate}+${changedTime}`;

    fetchJSON({
      settings: {
        method: "POST",
        url: api,
        body: JSON.stringify({
          date: correctDateFormat,
          cultureCode,
          isSK: true,
        }),
      },
    })
      .then((resultData: any) => {
        if (resultData.payload.data.length > 0) {
          setHasTableError(false);
          setTableItems(resultData.payload.data);
          setInputDate(resultData.payload.dateDll);
          setInputTimeObject(resultData.payload.timeDll);
          setQueryDate(resultData.payload.isQueryDate);
          setQueryDateText(resultData.payload.localization.releaseTimeNoRecord);
          setQueryDateValue(resultData.payload.queryDate);
        } else {
          setHasTableError(true);
          throw new Error("Payload 'fetchDateTime' not loaded!");
        }
      })
      .catch((error) => {
        setHasTableError(true);
        if (!isProduction) {
          throw new Error(error);
        }
      });
  };

  /**
   * Takes value from 'date' input onChange and passes it to:
   * 1. set new value for useState setInputDate()
   * 2. fetchDate()
   * 3. URL to show selected date
   * @param {string} changedDate - Date value from input onChange event.
   *
   * @returns {void}
   */
  const handleChangeDate = (changedDate: string) => {
    setInputDate(changedDate);
    fetchDate(changedDate);
    window.history.replaceState(null, "", `?date=${changedDate}`);
  };

  /**
   * Takes value from 'time' input onChange and passes it to:
   * 1. set new value for useState setInputTimeString()
   * 2. fetchDateTime()
   * 3. URL to show current date and selected time
   * @param {string} changedTime - Time value from select onChange event.
   *
   * @returns {void}
   */
  const handleChangeTime = (changedTime: string) => {
    setInputTimeString(changedTime);
    fetchDateTime(inputDate, changedTime);
    window.history.replaceState(null, "", `?date=${inputDate}%20${changedTime}`);
  };

  // DATEPICKER
  const hasDateError = !queryDateValue; // shows error if there is no exchange rate for given day [!false (is true) = show, !true (is false) = hide]
  const getErrorBorderColor = () => (hasDateError ? "border-red-corporate" : "border-grey-30");
  const getErrorMarginBottom = () => (hasDateError ? "mb-0" : "mb-6 sm:mb-0");

  // DATEPICKER INPUT
  // year
  const TEN_YEARS = 10;
  const currentYear = new Date().getFullYear();
  const tenYearsInThePast = currentYear - TEN_YEARS;

  // month
  const month = new Date().getMonth() + 1;
  const monthString = `0${month}`.slice(-2);

  // day
  const day = new Date().getDate();
  const dayString = `0${day.toString()}`.slice(-2);

  // values: min, max
  const datePickerMinValue = `${tenYearsInThePast}-01-01`;
  const datePickerMaxValue = `${currentYear}-${monthString}-${dayString}`;

  return (
    <>
      <FormWrapper>
        <form id={EXCHANGE_RATES_FORM} data-testid={EXCHANGE_RATES_FORM} method="POST" noValidate>
          <div className="flex w-full flex-col sm:flex-row">
            <div className="sm:mr-8 sm:w-1/2">
              <label htmlFor={DATE_PICKER} className="text-[12px]">
                {getLocalization(localization, "releaseDay")}
                <div className="relative">
                  <input
                    id={DATE_PICKER}
                    name={DATE_PICKER}
                    data-testid={DATE_PICKER}
                    type="date"
                    className={`exchange-rates-date-picker ${getErrorBorderColor()} ${getErrorMarginBottom()}`}
                    value={inputDate}
                    min={datePickerMinValue}
                    max={datePickerMaxValue}
                    onChange={(event) => {
                      handleChangeDate(event.target.value);
                    }}
                  />
                  <div className="pointer-events-none absolute right-[3px] top-[8px] w-[32px] bg-white">
                    <Icon
                      name={calendar.name}
                      viewBox={calendar.viewbox}
                      alt={calendar.alt}
                      svg={calendar.svg}
                      className="inline h-[21.5px] w-[19.5px] bg-white fill-red-corporate"
                    />
                  </div>
                </div>
              </label>
              {hasDateError && (
                <div className="mb-6 text-xs text-red-corporate sm:mb-0">
                  {getLocalization(localization, queryDateText)}
                </div>
              )}
            </div>

            <div className="sm:w-1/2">
              <label htmlFor={TIME_SELECT} className="text-[12px]">
                {getLocalization(localization, "releaseTime")}
                <select
                  id={TIME_SELECT}
                  name={TIME_SELECT}
                  data-testid={TIME_SELECT}
                  className="exchange-rates-time-select"
                  onChange={(event) => {
                    handleChangeTime(event.target.value);
                  }}
                >
                  {inputTimeObject.map((time, index) => {
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <option value={time.value} key={index} selected={time.isSelected}>
                        {time.text}
                      </option>
                    );
                  })}
                </select>
              </label>
            </div>
          </div>
        </form>
      </FormWrapper>

      <div className="container">
        <div className="row">
          <div className="sm:col-22 sm:offset-1">
            <div>
              {hasTableError ? (
                <Alert
                  type={AlertType.Error}
                  text={getLocalization(localization, "TableError")}
                  textAttributes={{ role: "alert", "aria-live": "assertive" }}
                  className="mb-12"
                />
              ) : (
                <RatesTable items={tableItems} localization={localization} type={tableType} />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="row">
          <div className="sm:col-14 sm:offset-1">
            <RatesDownloads
              isVisible={downloads.isVisible}
              title={downloads.title}
              links={downloads.links}
              iconsList={iconsList}
              apiUrl={downloads.apiUrl}
              cultureCode={cultureCode}
              fromDate={queryDateValueString}
              isSK={isSK}
            />
            <RatesKBvsECB
              exchangeRatesECBDifference={exchangeRatesECBDifference}
              inputDate={inputDate}
              iconsList={iconsList}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ExchangeRatesListPageLayout;
